import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import NewsletterSignup from '../components/NewsletterSignup';
import BackgroundAnimation from '../components/BackgroundAnimation';
import ScrollToTop from '../components/ScrollToTop';
import ProjectCard from '../components/ProjectCard';

const HeroSection = styled.section`
  background: linear-gradient(to right, #1a1a2e, #16213e);
  color: #fff;
  padding: 8rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: #ccc;
`;

const HeroButtonsContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeroButton = styled(Link)`
  padding: 0.8rem 2rem;
  border-radius: 5px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &.primary {
    background: #0066cc;
    color: #fff;
    
    &:hover {
      background: #004c99;
    }
  }
  
  &.secondary {
    background: transparent;
    color: #fff;
    border: 2px solid #fff;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const ProjectsSection = styled.section`
  padding: 5rem 2rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: #0066cc;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

// These styled components were previously used for a custom card implementation
// Now we're using the ProjectCard component imported at the top


const Home = () => {
  // Project data for ProjectCard component
  const projects = [
    {
      id: 'mcp-zero',
      title: 'MCP Zero',
      description: 'Production-grade AI agent infrastructure with resource constraints, ethical governance, and cryptographic integrity',
      image: '/assets/images/mcp-zero-bg.jpg',
      tags: ['AI', 'Infrastructure', 'Security'],
      badges: [{ text: 'Production Ready', color: '#10b981' }],
      features: [
        'Offline-First Architecture',
        'Advanced AI Agent Systems',
        'Ethical Governance Framework'
      ],
      accentColor: '#0066cc',
      link: '/mcp-zero'
    },
    {
      id: 'metanode',
      title: 'MetaNode Blockchain',
      description: 'Blockchain & dApp Deployment Infrastructure with vPod Container Integration',
      image: '/assets/images/metanode-bg.jpg',
      tags: ['Blockchain', 'dApps', 'Security'],
      badges: [{ text: 'Beta', color: '#8b5cf6' }],
      features: [
        'Ethereum-grade Security',
        'dApp Integration',
        'vPod Container Technology'
      ],
      accentColor: '#673ab7',
      link: '/metanode'
    },
    {
      id: 'zk-physics',
      title: 'Zero Knowledge Physics Engine',
      description: 'A real-time, verifiable physics engine with formal verification and military-grade simulation capabilities',
      image: '/assets/images/zk-physics-bg.jpg',
      tags: ['Physics', 'Verification', 'Simulation'],
      badges: [{ text: 'Research', color: '#2c3e50' }],
      features: [
        'Formal Verification Layer',
        'Military-Grade Precision',
        'Cryptographic State Tracking'
      ],
      accentColor: '#2c3e50',
      link: '/zk-physics'
    }
  ];

  return (
    <div>
      <ScrollToTop />
      
      <HeroSection>
        <BackgroundAnimation 
          type="particles" 
          particleCount={50} 
          color="rgba(255,255,255,0.3)"
          speed={0.5}
        />
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Innovative Technology Solutions
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Explore our revolutionary projects in AI infrastructure, blockchain technology, and verifiable physics
          </HeroSubtitle>
          <HeroButtonsContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <HeroButton to="/mcp-zero" className="primary">Explore MCP Zero</HeroButton>
            <HeroButton to="/metanode" className="secondary">Discover MetaNode</HeroButton>
            <HeroButton to="/zk-physics" className="secondary">Zero Knowledge Physics</HeroButton>
          </HeroButtonsContainer>
        </HeroContent>
      </HeroSection>
      
      <ProjectsSection>
        <SectionTitle>Featured Projects</SectionTitle>
        <ProjectsGrid>
          {projects.map((project, index) => (
            <ProjectCard 
              key={project.id}
              image={project.image}
              title={project.title}
              description={project.description}
              tags={project.tags}
              badges={project.badges}
              features={project.features}
              link={project.link}
              accentColor={project.accentColor}
              buttonText="Learn More"
              animationDelay={index * 0.2}
            />
          ))}
        </ProjectsGrid>
      </ProjectsSection>
      
      <NewsletterSignup 
        title="Stay Updated on Our Projects"
        description="Subscribe to our newsletter to get the latest updates on MCP Zero, MetaNode, and Zero Knowledge Physics Engine."
        buttonText="Subscribe"
        buttonColor="#0066cc"
      />
    </div>
  );
};

export default Home;
